/*
    When updating, recreate this file's jsDoc and copy/paste it into MapStateContext.js
 */

import { find } from 'lodash';
import React from 'react';
import { useOrderContext } from '../../useOrderContext';
import { getInitialConfig } from './util';

/**
 *
 * @returns {{departureTime: unknown, setAddresses: (value: (((prevState: *[]) => *[]) | *[])) => void, addresses: *[], setGeocodingAddress: (value: (((prevState: boolean) => boolean) | boolean)) => void, setReverseGeocoding: (value: (((prevState: boolean) => boolean) | boolean)) => void, updatingDestination: boolean, setUpdatingDestination: (value: (((prevState: boolean) => boolean) | boolean)) => void, setDisabled: (value: (((prevState: boolean) => boolean) | boolean)) => void, setOrigin: (value: (((prevState: {color: string, orderRef: *, lng: *, icon: string, id: string, type: string, lat: *, info: [{name: string, desc: *}]}) => {color: string, orderRef: *, lng: *, icon: string, id: string, type: string, lat: *, info: [{name: string, desc: *}]}) | {color: string, orderRef: *, lng: *, icon: string, id: string, type: string, lat: *, info: [{name: string, desc: *}]})) => void, origin: {color: string, orderRef: *, lng: *, icon: string, id: string, type: string, lat: *, info: [{name: string, desc: *}]}, setDepartureTime: (value: unknown) => void, destination: {color: string, lng: *, icon: string, type: string, projectRef: *, allowRouting: boolean, updateLocation, draggable: boolean, orderRef: *, id: string, radius: number, lat: *, info: [{name: string, desc: string}]}, setCalculatingRoute: (value: (((prevState: boolean) => boolean) | boolean)) => void, setEffectiveAddress: (value: unknown) => void, setDestination: (value: (((prevState: {color: string, lng: *, icon: string, type: string, projectRef: *, allowRouting: boolean, updateLocation, draggable: boolean, orderRef: *, id: string, radius: number, lat: *, info: [{name: string, desc: string}]}) => {color: string, lng: *, icon: string, type: string, projectRef: *, allowRouting: boolean, updateLocation, draggable: boolean, orderRef: *, id: string, radius: number, lat: *, info: [{name: string, desc: string}]}) | {color: string, lng: *, icon: string, type: string, projectRef: *, allowRouting: boolean, updateLocation, draggable: boolean, orderRef: *, id: string, radius: number, lat: *, info: [{name: string, desc: string}]})) => void, setTrucks: (value: unknown) => void, waypoints: *[], initialAddress: unknown, effectiveRadius: unknown, setConfig: (value: unknown) => void, setEffectiveCoordinates: (value: (((prevState: ({lng: *, lat: *}|null)) => ({lng: *, lat: *}|null)) | {lng: *, lat: *})) => void, allowRouting: boolean, setEffectiveRadius: (value: unknown) => void, setMap: (value: unknown) => void, disabled: boolean, reverseGeocoding: boolean, trucks: unknown, configBusy: boolean, isModalVisible: unknown, map: unknown, geocodingAddress: boolean, setAccepting: (value: (((prevState: boolean) => boolean) | boolean)) => void, accepting: boolean, setRoute: (value: (((prevState: *[]) => *[]) | *[])) => void, canUpdate: boolean, effectiveAddress: unknown, setChosenAddress: (value: unknown) => void, calculatingRoute: boolean, initializing: boolean, setInitializing: (value: (((prevState: boolean) => boolean) | boolean)) => void, chosenAddress: unknown, setIsModalVisible: (value: unknown) => void, setWaypoints: (value: (((prevState: *[]) => *[]) | *[])) => void, effectiveCoordinates: {lng: *, lat: *}, route: *[], config: unknown, effectiveDestination: {color: string, lng: *, icon: string, type: string, projectRef: *, allowRouting: boolean, updateLocation, draggable: boolean, orderRef: *, id: string, radius: number, lat: *, info: [{name: string, desc: string}]}, setInitialAddress: (value: unknown) => void}}
 */
export const useMapState = readOnly => {
  const {
    form: { form },
    lists,
  } = useOrderContext();

  const initialConfig = getInitialConfig(
    form?.crn,
    form?.projectRef,
    find(lists?.locations?.list, { crn: form?.locationRef }),
    form?.address
  );

  const [accepting, setAccepting] = React.useState(false);
  const [departureTime, setDepartureTime] = React.useState(form?.startDateTime);
  const [calculatingRoute, setCalculatingRoute] = React.useState(false);
  const [updatingDestination, setUpdatingDestination] = React.useState(false);
  const [disabled, setDisabled] = React.useState(false);
  const [initialAddress, setInitialAddress] = React.useState(form?.address);
  const [origin, setOrigin] = React.useState(initialConfig?.origin);
  const [destination, setDestination] = React.useState(initialConfig?.destination);
  const [route, setRoute] = React.useState([]);
  const [initializing, setInitializing] = React.useState(false);
  const [config, setConfig] = React.useState(initialConfig);
  const [chosenAddress, setChosenAddress] = React.useState(null);
  const [effectiveRadius, setEffectiveRadius] = React.useState(form?.address?.coordinates?.radius);
  const [effectiveAddress, setEffectiveAddress] = React.useState(form?.address);
  const [map, setMap] = React.useState(null);
  const [waypoints, setWaypoints] = React.useState([]);
  const [geocodingAddress, setGeocodingAddress] = React.useState(false);
  const [reverseGeocoding, setReverseGeocoding] = React.useState(false);
  const [addresses, setAddresses] = React.useState([]);
  const [isModalVisible, setIsModalVisible] = React.useState(null);
  const [trucks, setTrucks] = React.useState(null);

  const [effectiveCoordinates, setEffectiveCoordinates] = React.useState(
    form?.address?.coordinates
      ? {
          lat: form?.address?.coordinates?.latitude,
          lng: form?.address?.coordinates?.longitude,
        }
      : destination?.lat && destination?.lng
      ? {
          lat: destination.lat,
          lng: destination.lng,
        }
      : null
  );

  const effectiveDestination = React.useMemo(() => {
    // if (!effectiveAddress || !effectiveCoordinates) return null;

    return {
      ...destination,
      lat: effectiveCoordinates?.lat,
      lng: effectiveCoordinates?.lng,
    };
  }, [destination, effectiveCoordinates]);

  const allowRouting = !readOnly && !!destination?.allowRouting;
  const canUpdate = !readOnly && !!destination?.updateLocation;
  const configBusy =
    accepting || initializing || geocodingAddress || reverseGeocoding || calculatingRoute || updatingDestination;

  return {
    allowRouting,
    canUpdate,
    configBusy,

    setDepartureTime,
    setCalculatingRoute,
    setUpdatingDestination,
    setDisabled,
    setInitialAddress,
    setOrigin,
    setDestination,
    setRoute,
    setInitializing,
    setConfig,
    setChosenAddress,
    setEffectiveRadius,
    setEffectiveAddress,
    setEffectiveCoordinates,
    setMap,
    setWaypoints,
    setGeocodingAddress,
    setReverseGeocoding,
    setAddresses,
    setIsModalVisible,
    setTrucks,
    setAccepting,

    accepting,
    trucks,
    departureTime,
    calculatingRoute,
    updatingDestination,
    disabled,
    initialAddress,
    origin,
    destination,
    route,
    initializing,
    config,
    chosenAddress,
    effectiveRadius,
    effectiveAddress,
    effectiveDestination,
    effectiveCoordinates,
    map,
    waypoints,
    geocodingAddress,
    reverseGeocoding,
    addresses,
    isModalVisible,
    readOnly,
  };
};
